import React, { useState, useContext } from 'react';
import { navigate } from '@reach/router';
import { Related as RelatedExemplar } from '../RelatedElement';
import { StyledSavedText } from '../Styled/StyledRelatedNav';
import { MarginWrapper } from '../Styled';
import { EARLY, DEVELOPING, CONSOLIDATING, EXTENDING } from '../../constants';
import { SAVED_PROFORMA, WRITING_PROFORMA } from '../../constants/navigation';
import { RelatedTexts, RelatedObject, RelatedType, TextTypes } from '../../types';
import { getRedirectLink } from './RedirectLink';
import { DeleteTool } from '../DeleteTool';
import { EditToolContext } from '../../contexts/editToolContext';
import defaultIWPImage from '../../assets/defaultIWPImage.png';
import defaultSavedIWPImage from '../../assets/defaultSavedIWPImage.png';
import defaultSavedExemplarImage from '../../assets/defaultSavedExemplarImage.png';
import { getStageColors } from '../../utils';

export interface IRelated {
  related: RelatedTexts;
  stage: string;
  page?: string;
  type?: string;
  id?: number;
  removeSavedFromRelated: (id: number) => void;
}

export const Related = (props: IRelated): JSX.Element => {
  const { related, stage, page, type, id, removeSavedFromRelated } = props;

  const { handleLeavePage } = useContext(EditToolContext);

  const textType = related.default.length ? related.default[0].textType : '';
  const showProforma = [EARLY, DEVELOPING, CONSOLIDATING, EXTENDING].includes(stage) && textType !== TextTypes.hybrid;
  const { main: mainColor, light: highlightColor } = getStageColors(stage);

  const proforma: RelatedObject = {
    id: 0,
    textType: textType,
    title: 'Interactive Writing Proforma',
    thumbnail: '',
    stage: stage,
    type: RelatedType.proformas,
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSavedData, setSelectedSavedData] = useState<RelatedObject>(proforma);

  const handleDeleteClick = (data: RelatedObject) => {
    setSelectedSavedData(data);
    setIsModalOpen(true);
  };

  const currentlyInView = (data: RelatedObject): boolean => {
    const dataType = data.type?.substring(0, data.type?.length - 1);

    if (page === WRITING_PROFORMA && data.id == proforma.id && type != SAVED_PROFORMA) {
      return true;
    }

    if (data.id == id && dataType == type && page != WRITING_PROFORMA) {
      return true;
    }

    if ([SAVED_PROFORMA].includes(type as string) && data.id == id && dataType == type) {
      return true;
    }

    return false;
  };

  const getDefaultImage = (type: string) =>
    ({
      [RelatedType.proformas]: defaultIWPImage,
      [RelatedType.saved_exemplars]: defaultSavedExemplarImage,
      [RelatedType.saved_proformas]: defaultSavedIWPImage,
    }[type] as string);

  const relatedExemplar = (relatedItem: RelatedObject, isDefault: boolean) => {
    const getImageSrc = (dataSrc: string) => (dataSrc ? dataSrc : getDefaultImage(relatedItem.type));
    const getLink = (isFullScreen = false) =>
      getRedirectLink(
        relatedItem.stage,
        relatedItem.textType,
        relatedItem.type,
        relatedItem.id,
        isFullScreen,
        relatedItem.page
      );

    return (
      <RelatedExemplar
        isRelated
        textType={textType}
        title={relatedItem.title}
        imgSrc={getImageSrc(relatedItem.thumbnail)}
        viewLink={getLink()}
        fullScreenLink={getLink(true)}
        relatedType={relatedItem.textType}
        isRelatedDefault={isDefault}
        isRelatedCurrentlyInView={currentlyInView(relatedItem)}
        handleView={(e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement> | BeforeUnloadEvent) =>
          handleLeavePage(() => navigate(getLink()), false, e)
        }
        handleFullScreen={(e?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement> | BeforeUnloadEvent) =>
          handleLeavePage(() => navigate(getLink(true)), false, e)
        }
        handleDelete={() => handleDeleteClick(relatedItem)}
      />
    );
  };

  return (
    <>
      {related.default.map((data: RelatedObject) => {
        return relatedExemplar(data, true);
      })}
      {showProforma ? relatedExemplar(proforma, true) : null}
      {related.saved.length > 0 ? <StyledSavedText>Your Saved Texts</StyledSavedText> : <></>}
      {related.saved.map((data: RelatedObject) => {
        return relatedExemplar(data, false);
      })}
      <MarginWrapper margin="0px auto 75px" />
      {isModalOpen && (
        <DeleteTool
          setShowModal={setIsModalOpen}
          savedData={selectedSavedData}
          mainColor={mainColor}
          highlightColor={highlightColor}
          onDelete={removeSavedFromRelated}
        />
      )}
    </>
  );
};
